import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { api } from '@/lib/api';
import { SocialLinks } from '@/components/SocialLinks';

interface PasswordCriteria {
  label: string;
  met: boolean;
}

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setAuth } = useAuthStore();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    tenantName: '',
    marketingConsent: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Password strength criteria
  const criteria = useMemo<PasswordCriteria[]>(() => {
    const pwd = formData.password;
    return [
      { label: t('register.criteria8Chars'), met: pwd.length >= 8 },
      { label: t('register.criteriaUppercase'), met: /[A-Z]/.test(pwd) },
      { label: t('register.criteriaLowercase'), met: /[a-z]/.test(pwd) },
      { label: t('register.criteriaNumber'), met: /[0-9]/.test(pwd) },
      { label: t('register.criteriaSpecial'), met: /[^A-Za-z0-9]/.test(pwd) },
    ];
  }, [formData.password, t]);

  const metCount = criteria.filter((c) => c.met).length;
  const strengthPercent = (metCount / criteria.length) * 100;

  const getStrengthLabel = () => {
    if (metCount === 0) return '';
    if (metCount <= 2) return t('register.weak');
    if (metCount <= 4) return t('register.medium');
    return t('register.strong');
  };

  const getStrengthColor = () => {
    if (metCount <= 2) return 'bg-red-500';
    if (metCount <= 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const isPasswordValid = metCount === criteria.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError(t('register.passwordRequirements'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const response = await api.post('/auth/register', registerData);
      setAuth(response.data.data);
      navigate('/onboarding');
    } catch (err: any) {
      setError(err.response?.data?.error || t('register.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
      <div className="w-full max-w-md mx-4 sm:mx-0 p-8 bg-[#12121a] rounded-2xl border border-white/10">
        <div className="text-center mb-8">
          <a href="https://r3converse.com" target="_blank" rel="noopener noreferrer">
            <img src="/logo-r3converse.svg" alt="r3Converse" className="h-12 w-auto mx-auto mb-4" />
          </a>
          <p className="text-gray-400 mt-2">{t('register.title')}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('register.fullName')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
              placeholder={t('register.fullNamePlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('register.companyName')}</label>
            <input
              type="text"
              value={formData.tenantName}
              onChange={(e) => setFormData({ ...formData, tenantName: e.target.value })}
              className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
              placeholder={t('register.companyNamePlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('register.email')}</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
              placeholder={t('register.emailPlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('register.password')}</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 pr-12 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
                placeholder={t('register.passwordPlaceholder')}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                aria-label={showPassword ? t('register.hidePassword') : t('register.showPassword')}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {formData.password.length > 0 && (
              <div className="mt-3 space-y-2">
                {/* Strength bar */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-[#1a1a25] rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getStrengthColor()} transition-all duration-300`}
                      style={{ width: `${strengthPercent}%` }}
                    />
                  </div>
                  <span className={`text-xs font-medium ${
                    metCount <= 2 ? 'text-red-400' : metCount <= 4 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {getStrengthLabel()}
                  </span>
                </div>

                {/* Criteria checklist */}
                <div className="space-y-1">
                  {criteria.map((criterion) => (
                    <div key={criterion.label} className="flex items-center gap-2 text-sm">
                      <span className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-xs ${
                        criterion.met
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-700 text-gray-500'
                      }`}>
                        {criterion.met ? '✓' : '○'}
                      </span>
                      <span className={criterion.met ? 'text-gray-300' : 'text-gray-500'}>
                        {criterion.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('register.confirmPassword')}</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={`w-full px-4 py-3 pr-12 bg-[#1a1a25] border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4] ${
                  formData.confirmPassword && formData.password !== formData.confirmPassword
                    ? 'border-red-500/50'
                    : formData.confirmPassword && formData.password === formData.confirmPassword
                    ? 'border-green-500/50'
                    : 'border-white/10'
                }`}
                placeholder={t('register.confirmPasswordPlaceholder')}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                aria-label={showConfirmPassword ? t('register.hidePassword') : t('register.showPassword')}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                )}
              </button>
            </div>
            {formData.confirmPassword && (
              <p className={`text-xs mt-1 ${
                formData.password === formData.confirmPassword ? 'text-green-400' : 'text-red-400'
              }`}>
                {formData.password === formData.confirmPassword ? t('register.passwordsMatch') : t('register.passwordsNoMatch')}
              </p>
            )}
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="marketingConsent"
              checked={formData.marketingConsent}
              onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
              className="mt-1 w-4 h-4 rounded border-white/10 bg-[#1a1a25] text-[#006cd4] focus:ring-[#006cd4]"
            />
            <label htmlFor="marketingConsent" className="text-sm text-gray-400 leading-relaxed cursor-pointer">
              {t('register.marketingConsent')}
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !isPasswordValid}
            className="w-full py-3 bg-[#006cd4] text-black font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
          >
            {loading ? t('register.creatingAccount') : t('register.createAccount')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-400">
          {t('register.hasAccount')}{' '}
          <a href="/login" className="text-[#006cd4] hover:underline">
            {t('register.signIn')}
          </a>
        </p>

        <SocialLinks />
      </div>
    </div>
  );
};
